import { useEffect, useState } from 'react';
import { klassenbuchApi } from '../../lib/klassenbuchApi';
import type { AuditEntry } from '../../lib/klassenbuchApi';

type Props = {
  entityType: 'teaching_unit' | 'attendance' | 'excuse' | 'homework' | 'announcement' | 'ack' | 'lock';
  entityId: number;
};

const ACTION_LABEL: Record<string, string> = {
  create: 'erstellt',
  update: 'geändert',
  delete: 'gelöscht',
  lock: 'abgeschlossen',
  unlock: 'wieder geöffnet',
  send: 'versendet',
  approve: 'genehmigt',
  reject: 'abgelehnt',
  acknowledge: 'zur Kenntnis genommen'
};

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
}

export default function AuditPanel({ entityType, entityId }: Props) {
  const [items, setItems] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    klassenbuchApi
      .audit(entityType, entityId)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || 'Audit konnte nicht geladen werden');
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId]);

  if (error) {
    return <div className="text-sm text-rose-700">{error}</div>;
  }
  if (!items) {
    return <div className="text-sm text-gray-500">Lade Audit-Daten…</div>;
  }
  if (!items.length) {
    return <div className="text-sm text-gray-500">Keine Einträge vorhanden.</div>;
  }

  return (
    <ul className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
      {items.map((e) => (
        <li key={e.id} className="text-sm border-l-2 border-gray-200 pl-3 py-1">
          <div className="text-gray-800">
            <span className="font-medium">{e.actor_name || e.actor_username || 'System'}</span>{' '}
            {ACTION_LABEL[e.action] || e.action}{' '}
            <span className="text-gray-500">({e.entity_type})</span>
          </div>
          <div className="text-xs text-gray-500">
            {formatDate(e.created_at)}
            {e.ip_address ? ` · ${e.ip_address}` : ''}
          </div>
          {(e.old_values || e.new_values) && (
            <details className="text-xs text-gray-600 mt-1">
              <summary className="cursor-pointer">Details</summary>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-gray-500">vorher</div>
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify(e.old_values, null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="text-gray-500">nachher</div>
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify(e.new_values, null, 2)}
                  </pre>
                </div>
              </div>
            </details>
          )}
        </li>
      ))}
    </ul>
  );
}
